import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { NotificationProvider } from "./contexts/NotificationContext";
import Sidebar from "./components/Sidebar";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Contacts from "./pages/Contacts";
import Blast from "./pages/Blast";
import Campaigns from "./pages/Campaigns";
import CampaignEditor from "./pages/CampaignEditor";
import Templates from "./pages/Templates";
import Inbox from "./pages/Inbox";
import Settings from "./pages/Settings";
import Help from "./pages/Help";
import Team from "./pages/Team";
import FubWidget from "./pages/FubWidget";
import FubCampaigns from "./pages/FubCampaigns";
import Groups from "./pages/Groups";
import AiDrafts from "./pages/AiDrafts";
import Privacy from "./pages/Privacy";
import ForgotPassword from "./pages/ForgotPassword";
import Landing from "./pages/Landing";
import { Menu } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: "linear-gradient(180deg, #EFF7FF 0%, #FFFFFF 100%)" }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
          <p className="text-slate-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  // Redirect to onboarding if not completed
  if (user.onboarding_completed === false) return <Navigate to="/onboarding" replace />;
  return children;
}

function OnboardingRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: "linear-gradient(180deg, #EFF7FF 0%, #FFFFFF 100%)" }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  // If already onboarded, go to dashboard
  if (user.onboarding_completed !== false) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: "linear-gradient(180deg, #EFF7FF 0%, #FFFFFF 100%)" }}>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed md:relative inset-y-0 left-0 z-50
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
      `}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 shrink-0" style={{ backgroundColor: "#FFFFFF" }}>
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-9 h-9 flex items-center justify-center transition-colors"
            style={{ borderRadius: "11px" }}
            data-testid="mobile-menu-btn"
          >
            <Menu size={20} style={{ color: "#172937" }} />
          </button>
          <img src="/logo.svg" alt="Relay" className="h-8 w-auto" />
          <div className="w-9" />
        </header>

        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  if (user === undefined) return (
    <div className="flex items-center justify-center h-screen" style={{ background: "linear-gradient(180deg, #EFF7FF 0%, #FFFFFF 100%)" }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "#FF5C35", borderTopColor: "transparent" }} />
    </div>
  );

  return (
    <Routes>
      <Route path="/login" element={user ? (user.onboarding_completed === false ? <Navigate to="/onboarding" replace /> : <Navigate to="/dashboard" replace />) : <Login />} />
      <Route path="/register" element={user ? (user.onboarding_completed === false ? <Navigate to="/onboarding" replace /> : <Navigate to="/dashboard" replace />) : <Register />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <ForgotPassword />} />
      <Route path="/onboarding" element={<OnboardingRoute><Onboarding /></OnboardingRoute>} />
      <Route path="/" element={user ? <Navigate to="/dashboard" replace /> : <Landing />} />
      <Route path="/dashboard" element={<ProtectedRoute><AppLayout><Dashboard /></AppLayout></ProtectedRoute>} />
      <Route path="/contacts" element={<ProtectedRoute><AppLayout><Contacts /></AppLayout></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><AppLayout><Groups /></AppLayout></ProtectedRoute>} />
      <Route path="/blast" element={<ProtectedRoute><AppLayout><Blast /></AppLayout></ProtectedRoute>} />
      <Route path="/campaigns" element={<ProtectedRoute><AppLayout><Campaigns /></AppLayout></ProtectedRoute>} />
      <Route path="/campaigns/new" element={<ProtectedRoute><AppLayout><CampaignEditor /></AppLayout></ProtectedRoute>} />
      <Route path="/campaigns/:id/edit" element={<ProtectedRoute><AppLayout><CampaignEditor /></AppLayout></ProtectedRoute>} />
      <Route path="/fub-campaigns" element={<ProtectedRoute><AppLayout><FubCampaigns /></AppLayout></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><AppLayout><Templates /></AppLayout></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute><AppLayout><Inbox /></AppLayout></ProtectedRoute>} />
      <Route path="/ai-drafts" element={<ProtectedRoute><AppLayout><AiDrafts /></AppLayout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><AppLayout><Settings /></AppLayout></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><AppLayout><Team /></AppLayout></ProtectedRoute>} />
      <Route path="/help" element={<ProtectedRoute><AppLayout><Help /></AppLayout></ProtectedRoute>} />
      <Route path="/fub-widget" element={<FubWidget />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="*" element={user ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <NotificationProvider>
          <AppRoutes />
        </NotificationProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
